import { useEffect } from 'react'

import { isDriverTrackingAllowed } from 'components/booking-tracking/helpers'
import {
  DriverLocation,
  useGetBookingLazyQuery,
  useGetDriversLocationsQuery,
  useGetLoadCurrentLocationQuery,
  useGetSupplierBookingLazyQuery,
  useGetUserBookingLazyQuery,
} from 'generated/graphql'
import { useAllAddressesEta } from 'hooks'
import { usePermission } from 'providers/PermissionProvider'

import { MapWithReorder } from './MapWithReorder'

interface IProps {
  bookingId: string | null
  isQuote?: boolean
  courierExchangeLoadId?: string | null
}

const MapRoute = (props: IProps) => {
  const { bookingId, isQuote = false, courierExchangeLoadId = null } = props

  const [isAllowedToViewBookingFee] = usePermission('ViewBookingFeeInfoMargin')
  const [isAllowedToViewCustomerBooking] = usePermission('ViewUserBookingDetails')
  const [isAllowedToViewDriverLocation] = usePermission('ViewDriverLocation')

  // queries
  const [getAdminBooking, { data: adminBookingData }] = useGetBookingLazyQuery({
    variables: {
      bookingId: Number(bookingId),
    },
  })

  const [getUserBooking, { data: userBookingData }] = useGetUserBookingLazyQuery({
    variables: {
      bookingId: Number(bookingId),
    },
  })

  const [getSupplierBooking, { data: supplierBookingData }] = useGetSupplierBookingLazyQuery({
    variables: {
      bookingId: Number(bookingId),
    },
  })

  const booking =
    adminBookingData?.booking ??
    userBookingData?.userBooking ??
    supplierBookingData?.supplierBooking

  const { addressEtas } = useAllAddressesEta(bookingId, 60000)

  const {
    data: ourDriversLocationsData,
    startPolling: startPollingOurDrivers,
    stopPolling: stopPollingOurDrivers,
  } = useGetDriversLocationsQuery({
    fetchPolicy: 'network-only',
    skip: !isAllowedToViewDriverLocation || !isDriverTrackingAllowed(booking?.status),
    variables: { bookingId: Number(bookingId) },
  })

  const {
    data: cxDriverLocationsData,
    startPolling: startPollingCxDriver,
    stopPolling: stopPollingCxDriver,
  } = useGetLoadCurrentLocationQuery({
    fetchPolicy: 'network-only',
    variables: { loadId: Number(courierExchangeLoadId) },
    skip: !courierExchangeLoadId || !isDriverTrackingAllowed(booking?.status),
  })

  //* GetDriversLocations query can return [null]
  const driversLocations =
    ourDriversLocationsData?.driversLocations &&
    ourDriversLocationsData?.driversLocations?.filter((x) => x)?.length > 0
      ? (ourDriversLocationsData?.driversLocations as Array<DriverLocation>)
      : []

  if (courierExchangeLoadId && cxDriverLocationsData?.loadCurrentLocation)
    driversLocations.push(cxDriverLocationsData.loadCurrentLocation as DriverLocation)

  useEffect(() => {
    if (isAllowedToViewBookingFee) {
      getAdminBooking({
        variables: { bookingId: Number(bookingId) },
      })
    } else if (isAllowedToViewCustomerBooking) {
      getUserBooking({
        variables: { bookingId: Number(bookingId) },
      })
    } else {
      getSupplierBooking({ variables: { bookingId: Number(bookingId) } })
    }
  }, [
    bookingId,
    isAllowedToViewBookingFee,
    getAdminBooking,
    getUserBooking,
    isAllowedToViewCustomerBooking,
    getSupplierBooking,
  ])

  // Poll only when component is visible
  useEffect(() => {
    if (!isAllowedToViewDriverLocation || !isDriverTrackingAllowed(booking?.status)) {
      return
    }

    startPollingOurDrivers(2000)
    startPollingCxDriver(15000) // CX API Limitation: "Please don't send more than one request every 15 seconds."
    return () => {
      stopPollingOurDrivers()
      stopPollingCxDriver()
    }
  }, [
    startPollingOurDrivers,
    stopPollingOurDrivers,
    startPollingCxDriver,
    stopPollingCxDriver,
    isAllowedToViewBookingFee,
    isAllowedToViewDriverLocation,
    booking?.status,
  ])

  return (
    <MapWithReorder
      bookingId={bookingId}
      isQuote={isQuote}
      bookingDetails={booking}
      driverLocations={driversLocations}
      addressEtas={addressEtas}
    />
  )
}

export default MapRoute
